import React, { useState } from "react";
import { Eye, EyeOff, KeyRound } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface PasswordFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  id?: string;
  showStrengthLabel?: string;
}

export default function PasswordField({
  label,
  value,
  onChange,
  placeholder = "Enter password",
  id,
  showStrengthLabel,
}: PasswordFieldProps) {
  const [show, setShow] = useState(false);
  const [focused, setFocused] = useState(false);

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <label
          htmlFor={id}
          className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[rgb(var(--text-muted))]"
        >
          {label}
        </label>
        {showStrengthLabel && (
          <AnimatePresence mode="wait">
            <motion.span
              key={showStrengthLabel}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2 }}
              className="text-[10px] font-semibold uppercase tracking-wide text-gold-2"
            >
              {showStrengthLabel}
            </motion.span>
          </AnimatePresence>
        )}
      </div>
      <div
        className={`flex items-center gap-2 px-3 rounded-md border transition-all duration-200 ${
          focused
            ? "border-gold-2 ring-1 ring-gold-2/20 bg-[rgb(var(--surface-1))]"
            : "border-[rgb(var(--border-secondary))] bg-[rgb(var(--surface-1))]"
        }`}
        style={{ height: "42px" }}
      >
        <KeyRound size={13} className="text-[rgb(var(--text-muted))] shrink-0" />
        <input
          id={`password-field-input`}
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          className="password-field-input flex-1 bg-transparent border-none outline-none text-[13px] text-[rgb(var(--text-primary))] placeholder:text-[rgb(var(--text-muted))]"
          style={{ marginBottom: 0, height: "100%" }}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setShow((prev) => !prev)}
          className="shrink-0 flex items-center text-[rgb(var(--text-muted))] hover:text-[rgb(var(--text-primary))] transition-colors"
          aria-label={show ? "Hide password" : "Show password"}
        >
          {show
            ? <EyeOff size={14} style={{ display: "block" }} />
            : <Eye size={14} style={{ display: "block" }} />
          }
        </button>
      </div>
    </div>
  );
}
